import React, { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  CircleLoader,
  ErrorScreen,
  LogoIcon,
  PhoneWithControlAndPrefixInput,
  TextInputWithControl,
} from 'src/components';
import { useCustomerDeliveryRequest } from './useCustomerDeliveryRequest';

import { Controller } from 'react-hook-form';
import { TimeSlotSelector } from 'src/components/TimeSlotSelector';
import {
  DeliveryPublicColiInfoDataModel,
  OrganizationTimeSlot,
  OrganizationTimeSlotsObject,
} from 'src/features/DeliveryPublicColiInfo/delivery-public-colis-info.types';
import { useSubmitClientDeliveryRequest } from 'src/features/PublicClientDeliveryRequest/hooks';
import { NoticeBlock } from 'src/features/PublicClientDeliveryRequest/NoticeBlock';
import { Address, AddressAutocomplete } from '../AddressFRAutoComplete';
import { AdvisorCatalogLoader } from '../advisorCatalogLoader';
import { ShippingRecap } from './ShippingRecap';

type Props = {
  shippingId: string;
  code: string;
  tracker: string;
  shipping: DeliveryPublicColiInfoDataModel;
  timeSlots: OrganizationTimeSlotsObject;
  testID?: string;
};

export const CustomerDeliveryRequestContainer: React.FC<Props> = ({
  shippingId,
  shipping,
  code,
  timeSlots,
  tracker,
  testID = 'CustomerDeliveryRequestContainer',
}) => {
  const { control, setValue, errors, handleSubmit } = useCustomerDeliveryRequest({ defaultValues: {} });
  const { isLoading, error, data, submitClientDeliveryRequest } = useSubmitClientDeliveryRequest();
  const { t } = useTranslation();

  const [promoOpen, setPromoOpen] = useState(false); // State to manage accordion visibility

  const getCaptchaToken = useCallback(async () => {
    return await (window as unknown as any).grecaptcha.enterprise.execute('6LeBXJQqAAAAAP7LLrZyL81At37n9XuU8QApVK6k', {
      action: 'LOGIN',
    });
  }, []);

  const handleSelectionChange = useCallback(
    (selectedSlots: OrganizationTimeSlot[]) => {
      if (selectedSlots.length === 0) {
        return;
      }
      setValue('deliveryTimeSlot', selectedSlots);
    },
    [setValue],
  );

  const onSubmit = useCallback(
    async (data) => {
      try {
        const token = await getCaptchaToken();

        if (token) {
          submitClientDeliveryRequest({
            ...data,
            shippingId,
            captchaToken: token,
            tel: `+${data.tel}`,
            canal: 'APP_FRONT_SENDER',
          });
        }
      } catch (e) {
        alert(t('captcha-error'));
      }
    },
    [getCaptchaToken, t],
  );

  const onAddressSelect = useCallback(
    (address: Address) => {
      if (address) {
        setValue('address', address.label);
        setValue('zipCode', address.zipCode);
        setValue('city', address.city);
        setValue('lat', address?.geometry?.lat);
        setValue('long', address?.geometry?.lon);
      }
    },
    [setValue],
  );

  if (data) {
    return (
      <NoticeBlock
        type="success"
        text="client-delivery-requests.demande-created-success"
        code={code}
        tracker={tracker}
        shipping={shipping}
      />
    );
  }

  if (!timeSlots) {
    return <ErrorScreen error={new Error('Missing timeSlots')} />;
  }

  return (
    <div className="flex md:items-center md:justify-center min-h-screen bg-primary md:px-4 md:py-6">
      <div className=" bg-white px-2 py-2 md:p-10 relative w-auto">
        <LogoIcon className="w-40 md:w-40 my-2 m-auto" />
        <ShippingRecap shipping={shipping} />

        <script
          async
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6226260594799382"
          crossOrigin="anonymous"
        ></script>
        <ins
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-format="fluid"
          data-ad-layout-key="-if+f+o-ag+hi"
          data-ad-client="ca-pub-6226260594799382"
          data-ad-slot="2269656608"
        ></ins>
        <script>(adsbygoogle = window.adsbygoogle || []).push({});</script>
        <AdvisorCatalogLoader zone="TrackingUnderColis" />
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="mb-3 mx-auto space-y-4  justify-center md:align-middle md:w-4/5"
          id="delivery-request-form"
        >
          <div>
            <TextInputWithControl
              control={control}
              name="name"
              placeholder={t('client-delivery-requests.full-name')}
              error={errors.name}
              smallTitle
              showUpLabel
            />
          </div>

          <div>
            <TextInputWithControl
              control={control}
              name="email"
              placeholder={t('dashboard-agencies-edit.form-agency-email')}
              error={errors.email}
              showUpLabel
              type="email"
            />
          </div>

          <div>
            <PhoneWithControlAndPrefixInput
              control={control}
              name="tel"
              placeholder={t('dashboard-agencies-edit.form-agency-tel')}
              error={errors?.tel}
              showUpLabel
              smallTitle
              copyNumbersOnly={false}
            />
          </div>

          <div>
            <AddressAutocomplete error={errors.address} onAddressSelect={onAddressSelect} />
          </div>
          <div>
            <TextInputWithControl
              control={control}
              name="zipCode"
              placeholder={t('forms.zip')}
              error={errors.zipCode}
              showUpLabel
              smallTitle
            />
          </div>
          <div>
            <TextInputWithControl
              control={control}
              name="city"
              placeholder={t('forms.city')}
              error={errors.city}
              showUpLabel
              smallTitle
            />
          </div>
          <div>
            <Controller
              control={control}
              name="deliveryTimeSlot"
              render={({ field, fieldState: { error } }) => {
                return <TimeSlotSelector config={timeSlots} onSelectionChange={handleSelectionChange} error={error} />;
              }}
            />
          </div>

          {/* Promotion Accordion */}
          <div className="mt-6">
            <button
              type="button"
              onClick={() => setPromoOpen(!promoOpen)}
              className="w-full text-left py-3 px-4 bg-gray-100 text-gray-800 font-medium rounded-md shadow-md"
            >
              {t('client-delivery-requests.promo-accordion-title')}
            </button>
            {promoOpen && (
              <div className="mt-4">
                <TextInputWithControl
                  control={control}
                  name="promoCode"
                  placeholder={t('client-delivery-requests.promo-code-placeholder')}
                  error={errors.promoCode}
                  showUpLabel
                  uppercase
                />
              </div>
            )}
          </div>

          <div className="mt-6">
            <Controller
              control={control}
              name="optin"
              render={({ field, fieldState: { error } }) => {
                return (
                  <label
                    className={`cursor-pointer inline-block text-gray-600 font-regular ${error ? 'text-red-600' : ''}`}
                    htmlFor="senderIsNotReceiver"
                  >
                    <input
                      className={`mr-2 inline-block ${error ? 'border-red-600' : ''}`}
                      type="checkbox"
                      value="senderIsNotReceiver"
                      id="senderIsNotReceiver"
                      checked={field.value}
                      onChange={(e) => {
                        setValue('optin', e.target.checked);
                      }}
                    />
                    <Trans
                      i18nKey="client-delivery-requests.optin-consents"
                      components={{
                        custom: (
                          <a
                            href={`https://smartcolis.com/end-user-terms`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary underline"
                          />
                        ),
                      }}
                    />
                  </label>
                );
              }}
            />
          </div>

          {isLoading ? (
            <CircleLoader />
          ) : (
            <div className="mt-4 text-center mb-4">
              {!!error && <div className="text-red-600 text-center my-1">{t(error.message)}</div>}
              <button
                type="submit"
                className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold text-lg rounded-lg shadow-lg transform transition-transform duration-300 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-300"
              >
                {t('client-delivery-requests.submit-request')}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
